import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/alertApi';
import type { SaveAlertConfigRequest } from '../types';

export const alertKeys = {
  all: ['alerts'] as const,
  config: (locationId: string) => [...alertKeys.all, 'config', locationId] as const,
  status: (locationId: string) => [...alertKeys.all, 'status', locationId] as const,
  history: (locationId: string) => [...alertKeys.all, 'history', locationId] as const,
  regions: () => [...alertKeys.all, 'ukraine-regions'] as const,
};

export function useAlertConfig(locationId: string | undefined) {
  return useQuery({
    queryKey: alertKeys.config(locationId ?? ''),
    queryFn: () => api.getAlertConfig(locationId!),
    enabled: !!locationId,
  });
}

export function useSaveAlertConfig(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: SaveAlertConfigRequest) => api.saveAlertConfig(locationId, request),
    onSuccess: (data) => qc.setQueryData(alertKeys.config(locationId), data),
  });
}

export function useToggleAlertEnabled(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enable: boolean) =>
      enable ? api.enableAlertConfig(locationId) : api.disableAlertConfig(locationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: alertKeys.config(locationId) });
      qc.invalidateQueries({ queryKey: alertKeys.status(locationId) });
    },
  });
}

export function useAlertStatus(locationId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: alertKeys.status(locationId ?? ''),
    queryFn: () => api.getAlertStatus(locationId!),
    enabled: !!locationId && enabled,
    refetchInterval: 30_000,
  });
}

export function useAlertHistory(locationId: string | undefined) {
  return useInfiniteQuery({
    queryKey: alertKeys.history(locationId ?? ''),
    queryFn: ({ pageParam }) => api.getAlertHistory(locationId!, pageParam, 20),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.pageSize < last.totalCount ? last.page + 1 : undefined),
    enabled: !!locationId,
  });
}

export function useUkraineRegions() {
  return useQuery({
    queryKey: alertKeys.regions(),
    queryFn: () => api.listUkraineRegions(),
    staleTime: 60 * 60_000,
  });
}
